import { useState, useEffect, useRef } from "react"
import { CalendarIcon } from "lucide-react"
import { format, parse, isValid } from "date-fns"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { Calendar } from "../components/ui/calendar"
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover"
import { cn } from "../lib/utils"
import { stripPresetConflicts } from "../lib/preset-utils"

function parseBoundDate(raw: unknown): Date | undefined {
  const value = String(raw ?? "")
  if (!value) return undefined
  const parsed = parse(value, "yyyy-MM-dd", new Date())
  return isValid(parsed) ? parsed : undefined
}

export function DatePickerRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent, itemContext } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain.
  const atomDefaults = useAtomDefaults("date-picker")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const elementRecord = element as unknown as Record<string, unknown>
  const authoredOpenRaw = elementRecord.open ?? elementRecord.defaultOpen
  const resolvedAuthoredOpen = typeof authoredOpenRaw === "string"
    ? resolveSymbol(authoredOpenRaw)
    : authoredOpenRaw
  const authoredOpen = resolvedAuthoredOpen === true || resolvedAuthoredOpen === "true"
  const [open, setOpen] = useState(authoredOpen)
  const previousElementRef = useRef(element)
  const [localDate, setLocalDate] = useBoundValue<Date | undefined>(
    element.bind,
    resolveSymbol,
    undefined,
    parseBoundDate,
  )

  // Base UI's portaled popover can preserve its open lifecycle through the
  // effect that adopts a new element snapshot. Adopt that snapshot during
  // render as well so the controlled Root never commits the stale local value.
  let renderedOpen = open
  if (previousElementRef.current !== element) {
    previousElementRef.current = element
    renderedOpen = authoredOpen
    if (open !== authoredOpen) setOpen(authoredOpen)
  }

  // A new Cogo element snapshot is an external state update even when its
  // boolean value equals the pre-interaction value. Preserve local interaction
  // while the snapshot identity is stable, then adopt the next snapshot.
  useEffect(() => {
    setOpen(authoredOpen)
  }, [element, authoredOpen])

  const placeholder = element.placeholder
    ? String(resolveSymbol(element.placeholder) ?? "")
    : "Select date"

  // Display format (visual only); event value always uses yyyy-MM-dd
  const displayFormat = element.displayFormat
    ? String(resolveSymbol(element.displayFormat) ?? "yyyy/MM/dd")
    : "yyyy/MM/dd"

  const handleSelect = (date: Date | undefined) => {
    setLocalDate(date)
    const value = date ? format(date, "yyyy-MM-dd") : ""
    // forward per-item itemContext (parity with button/checkbox) so a block-ref'd row keeps item identity
    dispatchEvent(element.id, "change", { ...(itemContext ?? {}), value })
    setOpen(false)
  }

  return (
    <Popover open={renderedOpen} onOpenChange={setOpen}>
      <PopoverTrigger
        data-slot="date-picker-trigger"
        data-empty={!localDate}
        className={cn(
          "cn-date-picker-trigger cn-button group/button inline-flex shrink-0 items-center whitespace-nowrap rounded-lg text-sm font-medium outline-none transition-all",
          "border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground",
          "h-9 px-4 py-2 has-[>svg]:px-3",
          "justify-start text-left font-normal data-[empty=true]:text-muted-foreground",
          stripPresetConflicts(element.className, undefined, element.designAuthorityClassName),
        )}
        data-testid={`date-picker-${element.id}`}
        data-atom-type="date-picker"
        data-variant={resolvedVariant}
        data-size={resolvedSize}
        data-open={renderedOpen ? "true" : "false"}
      >
        <CalendarIcon className="mr-2 h-4 w-4 opacity-50" />
        {localDate ? format(localDate, displayFormat) : placeholder}
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={localDate}
          onSelect={handleSelect}
        />
      </PopoverContent>
    </Popover>
  )
}
